/**
 * LoadingSpinner
 * Centered loading indicator with spinner animation
 */

import React from 'react';

export interface LoadingSpinnerProps {
    /** Loading message (optional) */
    message?: string;
}

/**
 * EAA-755: token-driven, so the loading state follows the active theme.
 *
 * These were literals (`#e5e7eb` track, `#3b82f6` head, `#6b7280` label, and a
 * `rgba(255,255,255,.9)` overlay below). The overlay was the visible one: it
 * painted a WHITE sheet over whatever was loading, so in dark mode every editor
 * and player flashed white before its content appeared. The literals also ignored
 * a tenant's palette entirely.
 *
 * The fallbacks keep the old light values, so a surface that ships no ACH tokens
 * renders exactly as before instead of losing its colour.
 */
const spinnerStyles = `
@keyframes h5p-loading-spin {
  to { transform: rotate(360deg); }
}
.h5p-loading-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid hsl(var(--border, 220 13% 91%));
  border-top-color: hsl(var(--primary, 193 100% 32%));
  border-radius: 50%;
  animation: h5p-loading-spin 0.8s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .h5p-loading-spinner {
    animation-duration: 2.4s;
  }
}
`;

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({ message }) => (
    <>
        <style>{spinnerStyles}</style>
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
            }}
        >
            <div className="h5p-loading-spinner" />
            {message && (
                <span
                    style={{
                        color: 'hsl(var(--muted-foreground, 210 10% 44%))',
                        fontSize: 14,
                    }}
                >
                    {message}
                </span>
            )}
        </div>
    </>
);

/**
 * Centered loading overlay with spinner animation
 */
export interface LoadingOverlayProps extends LoadingSpinnerProps {
    /** Overlay background. Defaults to the themed page background at 90% opacity. */
    background?: string;
    /** Whether to cover full area (absolute positioning) */
    overlay?: boolean;
}

export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
    message,
    background = 'color-mix(in srgb, hsl(var(--background, 0 0% 100%)) 90%, transparent)',
    overlay = true,
}) => (
    <div
        style={{
            ...(overlay && {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                minWidth: '100%',
                minHeight: '100%',
            }),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background,
            zIndex: 50,
        }}
    >
        <LoadingSpinner message={message} />
    </div>
);

export default LoadingSpinner;
